/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { object, func, shape, string, bool } from 'prop-types';
import { withRouter, NavLink } from 'react-router-dom';
import { connect } from 'react-redux';

import p from 'polyglot/polyglot';

import { createUrl } from 'utils/urlHelper';

import {
  AUTH_SUCCESS,
  G_AUTH_SUCCESS,
  FB_AUTH_SUCCESS,
  authenticate as authenticateAction,
  googleAuth as googleAuthAction,
  facebookAuth as facebookAuthAction,
} from 'actions/auth';

import { CLIENT } from 'actions/signup';

import { SIGNUP_STATUS } from 'reducers/user';

import AuthButton from 'components/AuthButton/AuthButton';
import Checkbox from 'components/Form/Input/Checkbox/Checkbox';

import EmailPasswordForm from 'components/EmailPasswordForm/EmailPasswordForm';

import styles from './authenticate.scss';

/**
 * This is the Authenticate component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class Authenticate extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    history: object.isRequired,
    facebookAuth: func.isRequired,
    googleAuth: func.isRequired,
    authenticate: func.isRequired,
    signup: shape({
      type: string.isRequired,
    }).isRequired,
    isInvite: bool,
  };

  static timeout = null;

  static defaultProps = {
    isInvite: false,
  };

  state = {
    agreed: false,
  };

  onClickDisclaimer = event => {
    const agreed = event.target.checked;

    this.setState({
      agreed,
    });
  };

  // Duplicate code in login component.
  fbAuth = () => {
    const { agreed } = this.state;
    const { facebookAuth } = this.props;

    if (!agreed) {
      return this.shakeDisclaimer();
    }

    this.setState({ showPasswordSignup: false });

    facebookAuth().then(({ type, payload }) => {
      if (type === FB_AUTH_SUCCESS) {
        this.authenticate(payload);
      } else {
        console.log('SOMETHING WENT WRONG WITH THE FACEBOOK REQUEST!');
      }
    });
  };

  // Duplicate code in login component.
  gAuth = () => {
    const { agreed } = this.state;
    const { googleAuth } = this.props;

    if (!agreed) {
      return this.shakeDisclaimer();
    }

    this.setState({ showPasswordSignup: false });

    googleAuth().then(({ type, payload }) => {
      if (type === G_AUTH_SUCCESS) {
        this.authenticate(payload);
      } else {
        console.log('SOMETHING WENT WRONG WITH THE GOOGLE REQUEST!');
      }
    });
  };

  // Almost duplicate code in login component.
  authenticate = data => {
    const { history, signup, authenticate } = this.props;
    const { ...rest } = data;

    console.log({
      ...rest,
      invite_id: signup.invite_id,
      signup_as: signup.type,
      language: 'en',
    });

    return new Promise(resolve => {
      authenticate({
        ...rest,
        invite_id: signup.invite_id,
        signup_as: signup.type,
        language: 'en', // TODO: What locale is to be used?
      }).then(({ type, payload }) => {
        // Needed for passwordForm.
        resolve(payload);

        if (
          type === AUTH_SUCCESS &&
          payload.signup_status !== SIGNUP_STATUS.APPLICATION_APPROVED
        ) {
          history.push(createUrl('signup'));
        }
      });
    });
  };

  shakeDisclaimer = () => {
    const { agreed } = this.state;

    if (!agreed) {
      this.disclaimer.classList.add(styles.shake);
    }

    clearTimeout(this.timeout);

    this.timeout = setTimeout(() => {
      if (this.disclaimer) {
        this.disclaimer.classList.remove(styles.shake);
      }

      if (this.gdpr) {
        this.gdpr.classList.remove(styles.shake);
      }
    }, 820);
  };

  renderHeader() {
    const { signup, isInvite } = this.props;
    const isClient = signup.type === CLIENT;

    if (isInvite) {
      return (
        <React.Fragment>
          <h2 className={styles.title}>
            {p.t('signup.authenticate.invite.tagline')}
          </h2>
          <p className={styles.description}>
            {p.t('signup.authenticate.invite.description')}
          </p>
        </React.Fragment>
      );
    }

    if (isClient) {
      return (
        <React.Fragment>
          <h2 className={styles.title}>
            {p.t('signup.authenticate.client.tagline')}
          </h2>
          <p className={styles.description}>
            {p.t('signup.authenticate.client.description')}
          </p>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <h2 className={styles.title}>
          {p.t('signup.authenticate.talent.tagline')}
        </h2>
        <p className={styles.description}>
          {p.t('signup.authenticate.talent.description')}
        </p>
      </React.Fragment>
    );
  }

  /**
   * Render Authenticate.
   *
   * @return {Object}
   */
  render() {
    const { agreed } = this.state;

    return (
      <section className={styles.wrapper}>
        {this.renderHeader()}

        <div className={styles.checkboxes}>
          <label
            ref={ref => {
              this.disclaimer = ref;
            }}
            className={styles.disclaimer}
            htmlFor="disclaimer-checkbox"
          >
            <Checkbox
              name="disclaimer-checkbox"
              value={agreed}
              onChange={this.onClickDisclaimer}
            />
            <p>
              {p.t('signup.authenticate.disclaimer_text')}{' '}
              <NavLink
                to={createUrl('textPage', 'terms')}
                target="_blank"
                className={styles.link}
              >
                {p.t('signup.authenticate.disclaimer_link')}
              </NavLink>
            </p>
          </label>
        </div>

        <div className={styles.options}>
          <div className={styles.passwordForm}>
            <EmailPasswordForm
              light
              wide
              disabled={!agreed}
              authenticate={this.authenticate}
            />

            {!agreed && (
              <button
                type="button"
                className={styles.formCover}
                onClick={this.shakeDisclaimer}
              />
            )}
          </div>

          <div className={styles.or}>
              <div className={styles.divider}></div>
              <span className={styles.dividerName}>or</span>
              <div className={styles.divider}></div>
            </div>

          <ul className={styles.menu}>
            <li>
              <AuthButton
                onClick={this.fbAuth}
                disabled={!agreed}
                type="facebook"
                showIcon={true}
                wide
              >
                Facebook
              </AuthButton>
            </li>
            <li>
              <AuthButton
                onClick={this.gAuth}
                disabled={!agreed}
                type="google"
                showIcon={true}
                wide
              >
                Google
              </AuthButton>
            </li>
          </ul>
        </div>
      </section>
    );
  }
}

export default withRouter(
  connect(
    ({ signup }) => ({ signup }),
    {
      authenticate: authenticateAction,
      googleAuth: googleAuthAction,
      facebookAuth: facebookAuthAction,
    }
  )(Authenticate)
);
